/**
 * @file terms-and-conditions.component.ts
 * @description Hard, non-dismissible Terms & Conditions gate. Mounted globally
 * in app.html. Visibility is a pure derivation of AuthService signals
 * (profileLoaded + user) compared by equality against CURRENT_TERMS_VERSION.
 * Accept persists via AuthService.acceptGeneralTerms and closes ONLY on a
 * server-confirmed success; Decline signs the user out. There is no dismiss
 * affordance (no backdrop click, no Escape, no close button).
 */
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { KirigamiButton, ModalFocusDirective } from '@kirigami/shared/ui';

import { AuthService } from '../../../services/auth.service';
import { ToastService } from '../../../services/toast.service';
import { CURRENT_TERMS_VERSION, mustAcceptGeneralTerms } from '../../../models/terms';

@Component({
  selector: 'app-terms-and-conditions',
  standalone: true,
  imports: [KirigamiButton, ModalFocusDirective],
  templateUrl: './terms-and-conditions.component.html',
  styleUrls: ['./terms-and-conditions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermsAndConditionsComponent {
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);

  readonly agreed = signal(false);
  readonly submitting = signal(false);
  readonly error = signal<string | null>(null);

  /**
   * Pure-derived visibility. Waits for profileLoaded() so the boot-time
   * rehydrated user (which lacks terms fields) cannot flash the overlay.
   */
  readonly visible = computed(
    () => this.auth.profileLoaded() && mustAcceptGeneralTerms(this.auth.user()),
  );

  setAgreed(checked: boolean): void {
    this.agreed.set(checked);
  }

  accept(): void {
    if (!this.agreed() || this.submitting()) return;
    this.submitting.set(true);
    this.error.set(null);
    this.auth.acceptGeneralTerms(CURRENT_TERMS_VERSION).subscribe({
      next: () => this.submitting.set(false), // visible() recomputes -> overlay closes
      error: (e: Error) => {
        this.submitting.set(false);
        this.error.set(e.message);
        this.toast.error('Could not record your acceptance. Please try again.');
      },
    });
  }

  decline(): void {
    this.auth.logout();
  }

  /** Legal gate: Escape must not dismiss it. Decline is the only exit. */
  onEscapePressed(): void {
    // Intentionally empty.
  }
}
